<template>
	<div class="timer" :class="{ 'timer--labelled': beforeText }">
		<span v-if="beforeText" class="timer__before-text">
			{{ beforeText }}
		</span>
		<div class="timer__content">
			<AppControlIcon
				v-if="hasIcon"
				class="timer__icon"
				:iconName="IconTypes.TIMER"
				:size="iconSize"
			/>
			<span class="timer__time" :style="{ fontSize: `${fontSize}px` }">
				{{ formatTime }}
			</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import AppControlIcon from "@/components/AppControlIcon.vue";
import { IconTypes } from "@/types/ui";
import { floorNumber } from "@/utils/number";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

interface Props {
	beforeText?: string;
	hasIcon?: boolean;
	iconSize?: number;
	fontSize?: number;
	time: number; // time in seconds
}

const {
	beforeText = null,
	iconSize = 12,
	fontSize = 13,
	time,
	hasIcon = true,
} = defineProps<Props>();

const timer = ref<number>(time);
const interval = ref<number>(0);
const emit = defineEmits(["timerFinished"]);

onMounted((): void => {
	interval.value = window.setInterval((): void => {
		timer.value = floorNumber(timer.value - 1);
	}, 1_000);

	checkTimerFinished();
});

onBeforeUnmount((): void => {
	clearInterval(interval.value);
});

const checkTimerFinished = (): boolean => {
	if (interval.value && timer.value <= 0) {
		emit("timerFinished");

		// After time is over
		clearInterval(interval.value);
		return true;
	} else {
		return false;
	}
};

const formatTime = computed((): string => {
	if (checkTimerFinished()) {
		return "0s";
	}

	const days = floorNumber(timer.value / 86400);
	const hours = floorNumber((timer.value % 86400) / 3600);
	const minutes = floorNumber((timer.value % 3600) / 60);
	const seconds = floorNumber(timer.value % 60);

	const parts = [];

	if (days) {
		parts.push(`${days}d`);
		if (hours) {
			parts.push(`${hours < 10 ? "0" : ""}${hours}h`);
		} else if (minutes) {
			parts.push(`${minutes < 10 ? "0" : ""}${minutes}m`);
		}
	} else if (hours) {
		parts.push(`${hours < 10 ? "0" : ""}${hours}h`);
		if (minutes) {
			parts.push(`${minutes < 10 ? "0" : ""}${minutes}m`);
		} else if (seconds) {
			parts.push(`${seconds < 10 ? "0" : ""}${seconds}s`);
		}
	} else if (minutes) {
		parts.push(`${minutes}m`);
		if (seconds) {
			parts.push(`${seconds < 10 ? "0" : ""}${seconds}s`);
		}
	} else if (seconds) {
		parts.push(`${seconds}s`);
	}

	return parts.join(" ");
});
</script>

<style lang="scss" scoped>
.timer {
	@include flex-row-center;
	gap: 0.25rem;
	text-shadow: 0.04rem 0.04rem 0.06rem rgba(0, 0, 0, 0.6);
	text-wrap: nowrap;

	&--labelled {
		// applied only when `beforeText` is present – ensures text, number, and icon share the same base alignment
		// NOTE: not suitable for time-only displays (without prefix text)
		align-items: baseline;
	}

	&__content {
		@include flex-row-start;
		align-items: baseline;
		gap: 0.063rem;
	}

	&__icon {
		position: relative;
		top: 0.125rem;
		filter: drop-shadow(0.04rem 0.04rem 0.06rem rgba(0, 0, 0, 0.6));
	}

	&__time {
		font-weight: 800;
	}
}
</style>
